"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.Modal = void 0;
var react_1 = __importStar(require("react"));
var react_dom_1 = require("react-dom");
var framer_motion_1 = require("framer-motion");
var mixins_1 = require("../../../themes/mixins");
var useScreenWidth_1 = require("../../../utils/customHooks/useScreenWidth");
var ModalHeaderStyle1_1 = require("./comps/ModalHeaderStyle1");
var ModalHeaderStyle2_1 = require("./comps/ModalHeaderStyle2");
var styled_1 = require("./styled");
var Buttons_1 = require("../../Buttons");
var Divider_1 = require("../../Divider");
/**
 * @component Renders a modal component with customizable title, content, and actions.
 *
 * - The props object containing the following properties:
 * @prop {string} title: The title of the modal.
 * @prop {() => void} onClose: A callback function to be called when the close button is clicked.
 * @prop {React.ReactNode} children: The content to be rendered inside the modal body.
 * @prop {string} [linkTo]: An optional link for the modal.
 * @prop {string} [desktopWidth]: The width of the modal on desktop screens. Defaults to "80%".
 * @prop {('style1' | 'style2')} [headerStyle]: The style of the modal header. Defaults to "style1".
 * @prop {string} [subtitle]: The subtitle of the modal.
 * @prop {boolean} [fitToContentHeight]: A boolean indicating whether the modal height should be adjusted based on its content. Defaults to false.
 * @prop {boolean} [invert]: A boolean indicating whether to invert the background color of the modal. Defaults to false.
 * @prop {boolean} [overflowVisible]: A boolean indicating whether the modal should render its overflow as visible. Defaults to false.
 * @prop {boolean} [showActionPanel]: A boolean indicating whether to render the action panel. Defaults to false.
 * @prop {string} [onCloseText]: The text to be displayed in the close button. Defaults to "Close".
 * @prop {React.MouseEventHandler<HTMLButtonElement> | React.ReactNode} [onConfirm]: A callback function or node to be rendered instead of the confirm button.
 * @prop {boolean} [showCloseBtn]: A boolean indicating whether to render the close button. Defaults to true.
 * @prop {string} [onConfirmText]: The text to be displayed in the confirm button. Defaults to "Confirm".
 * @prop {boolean} [showConfirmBtn]: A boolean indicating whether to render the confirm button. Defaults to false.
 * @prop {('left' | 'right' | 'center')} [alignActions]: The alignment of the action buttons. Defaults to "right".
 * @prop {boolean} [disableHeader]: A boolean indicating whether to render the header. Defaults to false.
 * @prop {boolean} [dynamicHeaderHeight]: A boolean indicating whether the header (style1) should take a fixed height of 4rem or increase height flexibly as per the text within it. Defaults to false.
 * @return {ReactElement} The rendered modal component.
 */
var Modal = function (_a) {
    var title = _a.title, onClose = _a.onClose, children = _a.children, linkTo = _a.linkTo, _b = _a.desktopWidth, desktopWidth = _b === void 0 ? "80%" : _b, _c = _a.headerStyle, headerStyle = _c === void 0 ? "style1" : _c, subtitle = _a.subtitle, _d = _a.fitToContentHeight, fitToContentHeight = _d === void 0 ? false : _d, invert = _a.invert, overflowVisible = _a.overflowVisible, _e = _a.showActionPanel, showActionPanel = _e === void 0 ? false : _e, _f = _a.onCloseText, onCloseText = _f === void 0 ? "Close" : _f, onConfirm = _a.onConfirm, _g = _a.showCloseBtn, showCloseBtn = _g === void 0 ? true : _g, _h = _a.onConfirmText, onConfirmText = _h === void 0 ? "Confirm" : _h, _j = _a.showConfirmBtn, showConfirmBtn = _j === void 0 ? true : _j, _k = _a.alignActions, alignActions = _k === void 0 ? "right" : _k, _l = _a.disableHeader, disableHeader = _l === void 0 ? false : _l, _m = _a.dynamicHeaderHeight, dynamicHeaderHeight = _m === void 0 ? false : _m;
    var screenWidth = (0, useScreenWidth_1.useScreenWidth)();
    var handleClose = function () { return onClose(); };
    var initAnimPos = { marginTop: "100dvh", opacity: 0 };
    var endAnimPos = { marginTop: 0, opacity: 1 };
    var _o = (0, react_1.useState)(endAnimPos), animPos = _o[0], setAnimPos = _o[1];
    (0, react_1.useEffect)(function () {
        document.body.style.overflow = "hidden";
        return function () { document.body.style.overflow = "unset"; };
    }, []);
    var genHeader = function () {
        var headerStyles = {
            style1: ModalHeaderStyle1_1.ModalHeaderStyle1,
            style2: ModalHeaderStyle2_1.ModalHeaderStyle2,
        };
        var Comp = headerStyles[headerStyle];
        return (react_1.default.createElement(Comp, { title: title, subtitle: subtitle, setAnimPos: setAnimPos, linkTo: linkTo, onClose: onClose, initAnimPos: initAnimPos, invert: invert, dynamicHeaderHeight: dynamicHeaderHeight }));
    };
    var takeFullScreenOnScroll = ["style1"].indexOf(headerStyle) !== -1
        ? true
        : ["style2"].indexOf(headerStyle) !== -1 && false;
    var genModalContents = function () { return (react_1.default.createElement(styled_1.StyledModalLargeContentWrapper, { className: "StyledModalLargeContentWrapper", desktopWidth: desktopWidth, takeFullScreenOnScroll: takeFullScreenOnScroll, style: !fitToContentHeight ? { minHeight: 'calc(100dvh - 4rem)' } : {} },
        !disableHeader && genHeader(),
        react_1.default.createElement(styled_1.StyledModalBodyWrapper, { style: !fitToContentHeight ? { minHeight: 'calc(100dvh - 8rem)' } : {}, overflowVisible: overflowVisible, takeFullScreenOnScroll: takeFullScreenOnScroll },
            react_1.default.createElement(styled_1.StyledModalLargeBody, { takeFullScreenOnScroll: takeFullScreenOnScroll }, children),
            showActionPanel &&
                react_1.default.createElement("div", { style: { position: 'sticky', bottom: headerStyle === 'style1' ? 0 : '2.2rem' } },
                    react_1.default.createElement(Divider_1.Divider, null),
                    react_1.default.createElement(styled_1.StyledActionButtonsWrapper, { alignActions: alignActions },
                        showConfirmBtn &&
                            typeof onConfirm === 'function'
                            ? react_1.default.createElement(Buttons_1.ButtonPrimary, { onClick: onConfirm }, onConfirmText)
                            : onConfirm,
                        showCloseBtn && react_1.default.createElement(Buttons_1.ButtonGhost, { onClick: onClose }, onCloseText)))))); };
    return ((0, react_dom_1.createPortal)(react_1.default.createElement(styled_1.StyledModalLargeBg, null,
        react_1.default.createElement(styled_1.StyledModalLargeBgColor, { initial: { opacity: initAnimPos.opacity }, animate: { opacity: animPos.opacity }, transition: {
                type: "tween",
                ease: "easeOut",
            }, onClick: function () { return handleClose(); } }),
        screenWidth < (0, mixins_1.getBreakPoint)("sm") ? (react_1.default.createElement(framer_motion_1.motion.div, { style: __assign({ position: "relative" }, (fitToContentHeight
                ? {
                    minHeight: '100dvh',
                    display: 'flex',
                    alignItems: 'flex-end'
                }
                : {})), initial: { marginTop: initAnimPos.marginTop }, animate: { marginTop: animPos.marginTop }, transition: {
                type: "tween",
                ease: "easeOut",
            }, onAnimationComplete: function () {
                if (animPos.marginTop === initAnimPos.marginTop)
                    handleClose();
            }, onClick: function (e) { return e.stopPropagation(); } }, genModalContents())) : (react_1.default.createElement(framer_motion_1.motion.div, { style: { width: "100%" }, initial: initAnimPos, animate: animPos, transition: {
                type: "tween",
                ease: "easeOut",
            }, onAnimationComplete: function () {
                if (animPos.marginTop === initAnimPos.marginTop)
                    handleClose();
            }, onClick: function (e) { return e.stopPropagation(); } }, genModalContents()))), document.getElementById("modal-root")));
};
exports.Modal = Modal;
